
import { useState } from 'react';
import { authPost } from '@client/shared/auth/index.ts';
import { Button, Card, Heading, Row, Stack, Text } from '@client/shared/design';

type RequestState = 'idle' | 'sending' | 'sent' | 'error';

async function requestPasswordReset(email: string): Promise<boolean> {
  try {
    const response = await authPost('/auth/password-reset', 'password-reset', { email });
    return response.ok;
  } catch {
    return false;
  }
}

export interface SecurityBlockProps {
  email: string;
}

export function SecurityBlock({ email }: SecurityBlockProps) {
  const [state, setState] = useState<RequestState>('idle');

  async function handleChangePassword() {
    setState('sending');
    const ok = await requestPasswordReset(email);
    setState(ok ? 'sent' : 'error');
  }

  return (
    <Card pad="lg" spine as="section" aria-labelledby="security-heading">
      <Stack gap={4}>
        <Stack gap={1}>
          <Heading level={3} as="h2" id="security-heading">
            Security
          </Heading>
          <Text size="sm" tone="soft">
            We’ll email a password-reset link to <strong>{email}</strong>. Follow it to choose a new
            password: nothing to type here.
          </Text>
        </Stack>

        {state === 'sent' ? (
          <Text size="sm" tone="accent" role="status">
            Check your inbox at {email}: a reset link is on its way. Use it soon; the link expires
            after a short time.
          </Text>
        ) : (
          <Row gap={3} align="center" wrap>
            <Button
              variant="secondary"
              loading={state === 'sending'}
              onClick={() => void handleChangePassword()}
            >
              Change password
            </Button>
            {state === 'error' && (
              <Text size="sm" tone="danger" role="alert">
                Couldn’t start the reset. Please try again.
              </Text>
            )}
          </Row>
        )}
      </Stack>
    </Card>
  );
}
